import { Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { imgSrc } from "@/lib/constants";

const HEATING_CARDS = [
  {
    title: "SOBE DE TERACOTĂ",
    tagline: "Construcție, montaj, reparații și soluții adaptate spațiului.",
    image: "/api/files/public/brox-construct/uploads/portfolio/cd8cda7d-89ce-4fa1-8188-01227f4a1455.jpg",
    testid: "heating-card-sobe",
  },
  {
    title: "ȘEMINEE",
    tagline: "Soluții pentru confort, încălzire și design.",
    image: "/api/files/public/brox-construct/uploads/portfolio/a66a535d-f4b0-4132-b3f4-2b9a9af4e159.jpg",
    testid: "heating-card-seminee",
  },
  {
    title: "POMPE DE CĂLDURĂ",
    tagline: "Soluții moderne pentru încălzire, răcire și eficiență energetică.",
    image: "https://images.unsplash.com/photo-1776860150272-653efc74193c?auto=format&fit=crop&q=85&w=1200",
    testid: "heating-card-pompe",
  },
];

const EXTERIOR_CARDS = [
  {
    title: "GRĂTARE & BUCĂTĂRII EXTERIOARE",
    tagline: "Grătare zidite, grill-uri și bucătării exterioare.",
    image: "https://images.unsplash.com/photo-1786882693301-e285ba835874?auto=format&fit=crop&q=85&w=1200",
    testid: "exterior-card-gratare",
  },
  {
    title: "GARDURI",
    tagline: "Garduri și delimitări pentru proprietatea ta.",
    image: "https://images.unsplash.com/photo-1613756178402-ed5c1ea492b4?auto=format&fit=crop&q=85&w=1200",
    testid: "exterior-card-garduri",
  },
  {
    title: "PORȚI METALICE",
    tagline: "Porți și lucrări metalice pentru exterior.",
    image: "https://images.unsplash.com/photo-1715794497793-9c0dd2a5488f?auto=format&fit=crop&q=85&w=1200",
    testid: "exterior-card-porti",
  },
  {
    title: "TERASE & PAVAJE",
    tagline: "Terase și pavaje rezolvate din prima.",
    image: "https://images.pexels.com/photos/17366756/pexels-photo-17366756.jpeg?auto=format&fit=crop&q=85&w=1200",
    testid: "exterior-card-terase",
  },
  {
    title: "AMENAJĂRI CURȚI",
    tagline: "Transformăm curtea într-un spațiu pe care îl folosești.",
    image: "https://images.unsplash.com/photo-1696846911635-83b7e53fb65?auto=format&fit=crop&q=85&w=1200",
    testid: "exterior-card-curți",
  },
];

const B2B_TARGETS = ["Pensiuni", "Apartamente în regim hotelier", "Spații comerciale", "Birouri", "Proprietăți administrate", "Case de vacanță"];

function CardRow({ cards }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="extras-cards">
      {cards.map((c) => (
        <Reveal key={c.title} className="h-full">
          <div className="card-brox group h-full overflow-hidden hover:border-copper/50">
            <div className="relative h-40 overflow-hidden">
              <img src={imgSrc(c.image)} alt={`${c.title} — Brox Construct`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-800 via-transparent to-transparent" aria-hidden="true" />
            </div>
            <div className="p-5">
              <h4 className="display-title text-xl text-paper">{c.title}</h4>
              <p className="mt-2 text-sm text-muted-foreground">{c.tagline}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

export default function ServiceExtras({ slug }) {
  const { openQuote } = useSite();

  if (slug === "instalatii") {
    return (
      <section className="bg-ink-950 py-12 sm:py-16" data-testid="heating-extras">
        <div className="container-brox">
          <Reveal><p className="eyebrow mb-3">ÎNCĂLZIRE & TERMIC</p></Reveal>
          <Reveal delay={0.05}><h3 className="display-title mb-8 text-3xl sm:text-4xl">Soluții de încălzire pentru fiecare casă</h3></Reveal>
          <CardRow cards={HEATING_CARDS} />
          <Reveal delay={0.2}>
            <Link to="/proiecte" className="group mt-6 inline-flex items-center gap-2 border border-copper/50 bg-copper/10 px-6 py-3 font-display text-base font-bold uppercase tracking-wider text-copper transition-all duration-300 hover:bg-copper hover:text-ink-950" data-testid="heating-realworks-link">
              Vezi lucrări reale
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    );
  }

  if (slug === "exterior") {
    return (
      <section className="bg-ink-950 py-12 sm:py-16" data-testid="exterior-extras">
        <div className="container-brox">
          <Reveal><p className="eyebrow mb-3">AMENAJĂRI EXTERIOARE</p></Reveal>
          <Reveal delay={0.05}><h3 className="display-title mb-8 text-3xl sm:text-4xl">Exteriorul, terminat până la cel mai mic detaliu</h3></Reveal>
          <CardRow cards={EXTERIOR_CARDS} />
        </div>
      </section>
    );
  }

  if (slug === "interventii-mentenanta") {
    return (
      <section className="bg-ink-950 py-12 sm:py-16" data-testid="maintenance-b2b-extras">
        <div className="container-brox max-w-5xl">
          <Reveal><p className="eyebrow mb-3 flex items-center gap-2"><Briefcase className="h-4 w-4" /> PENTRU PROPRIETĂȚI ȘI AFACERI</p></Reveal>
          <Reveal delay={0.05}>
            <h3 className="display-title text-3xl sm:text-4xl">Mentenanță pentru proprietăți și afaceri</h3>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg" data-testid="maintenance-b2b-copy">
              Apare o problemă? Nu mai pierde timp căutând separat instalator, electrician sau meseriaș.
              Contactezi BROX CONSTRUCT, ne explici problema, iar noi identificăm soluția și omul potrivit.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-6 flex flex-wrap gap-1.5" data-testid="maintenance-b2b-targets">
              {B2B_TARGETS.map((t) => (
                <span key={t} className="border border-white/10 bg-ink-900 px-3 py-1.5 text-xs text-paper/75">{t}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <button
              onClick={() => openQuote({ request_type: "B2B_MAINTENANCE", source: "interventii-b2b" })}
              className="btn-primary copper-sheen mt-8 w-full sm:w-auto"
              data-testid="maintenance-b2b-cta"
            >
              AM NEVOIE DE O INTERVENȚIE
            </button>
          </Reveal>
        </div>
      </section>
    );
  }

  return null;
}
